import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';

import { useTheme } from '../../ThemeContext';

import Helmet from '../../components/Helmet';
import LoadingData from '../../components/LoadingData';
import Header from '../../components/Header';
import HeaderLabel from '../../components/HeaderLabel';
import Sider from '../../components/Sider';
import DataSource from '../../components/DataSource';
import DataSourceEmpty from '../../components/DataSource/DataSourceEmpty';
import DataSourceManage from '../../components/DataSourceAdd';
import DataSourceDelete from '../../components/DataSourceDelete';

import {
  Section,
  Wrapper,
  Content,
  Panel,
  PanelInner,
  Information,
  Text,
  Button,
  Center,
  Box,
} from './DataSourcesPage.style';

const DataSourcesPage = () => {
  const intl = useIntl();
  const theme = useTheme();
  const { profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const dataSources = useSelector(
    (state) => state.firestore.ordered['data_sources']
  );
  const [visibleDataSourcesAddModal, setVisibleDataSourcesAddModal] = useState(
    false
  );
  const [selectedDataSource, setSelectedDataSource] = useState(undefined);
  const [
    visibleDataSourcesDeleteModal,
    setVisibleDataSourcesDeleteModal,
  ] = useState(false);
  const [dataSourceDeleteProcess, setDataSourceDeleteProcess] = useState(false);

  const countDataSources = (dataSources && dataSources.length) || 0;

  const handleAction = (action = {}) => {
    if (action.type === 'CREATE' || action.type === 'EDIT') {
      setVisibleDataSourcesAddModal(true);
      setSelectedDataSource(action.payload);
    } else if (action.type === 'DELETE') {
      setVisibleDataSourcesDeleteModal(true);
      setSelectedDataSource(action.payload);
    } else {
      setVisibleDataSourcesAddModal(false);
      setVisibleDataSourcesDeleteModal(false);
      setSelectedDataSource(undefined);
    }
  };

  const handleDeleteProcess = (status) => setDataSourceDeleteProcess(status);

  const firestoreConnect = [];
  if (isProfileCompany) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        { collection: 'data_sources', orderBy: [['createdAt', 'desc']] },
      ],
      storeAs: 'data_sources',
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'statistics',
          doc: 'overall',
        },
      ],
      storeAs: 'statistics',
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    if (
      (visibleDataSourcesAddModal || visibleDataSourcesDeleteModal) &&
      selectedDataSource &&
      selectedDataSource.id &&
      dataSources &&
      dataSources.length > 0
    ) {
      const dataSource = dataSources.find(
        (dataSource) => String(dataSource.id) === String(selectedDataSource.id)
      );
      if (!dataSource) {
        if (visibleDataSourcesAddModal) {
          setVisibleDataSourcesAddModal(false);
          setSelectedDataSource(undefined);
        }
        if (visibleDataSourcesDeleteModal && !dataSourceDeleteProcess) {
          setVisibleDataSourcesDeleteModal(false);
          setSelectedDataSource(undefined);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSources]);

  const renderCountPhotos = () => {
    return (
      <HeaderLabel>
        {countDataSources}{' '}
        {intl.formatMessage({
          id: 'DATA_SOURCES.DATA_SOURCES_CONFIGURED',
        })}
      </HeaderLabel>
    );
  };

  const renderInformation = (information, children) => {
    return (
      <Center>
        <Information theme={theme.content.information.default}>
          <Text>{information}</Text>
          {children ? children : ''}
        </Information>
      </Center>
    );
  };

  const renderPanelBox = () => {
    return (
      <Box>
        <DataSourceEmpty handleAction={(action) => handleAction(action)} />
        {dataSources.map((source) => (
          <DataSource
            key={source.id}
            data={source}
            handleAction={(action) => handleAction(action)}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'PAGE_TITLE.DATA_SOURCES' })} />
      <Section>
        <Sider />
        <Wrapper>
          <Header title={intl.formatMessage({ id: 'PAGE_TITLE.DATA_SOURCES' })}>
            {countDataSources > 0 ? renderCountPhotos() : ''}
          </Header>
          <Content>
            <Panel theme={theme.content.panel.default}>
              <PanelInner>
                {!isProfileCompany ? (
                  renderInformation(
                    intl.formatMessage({
                      id: `DATA_SOURCES.YOU_HAVE_NO_ASSIGNED_COMPANY`,
                    })
                  )
                ) : isLoaded(dataSources) ? (
                  !(dataSources && dataSources.length > 0) ? (
                    renderInformation(
                      intl.formatMessage({
                        id: `DATA_SOURCES.YOU_HAVE_NO_DATA_SOURCES_CONFIGURED`,
                      }),
                      <Button
                        theme={theme.button.default}
                        onClick={() => handleAction({ type: 'CREATE' })}
                      >
                        {intl.formatMessage({
                          id: `DATA_SOURCES.ADD_A_NEW_ONE`,
                        })}
                      </Button>
                    )
                  ) : (
                    renderPanelBox()
                  )
                ) : (
                  <LoadingData fullmode={true} />
                )}
              </PanelInner>
            </Panel>
          </Content>
        </Wrapper>
      </Section>
      {visibleDataSourcesAddModal && (
        <DataSourceManage
          data={selectedDataSource}
          handleClose={() => handleAction()}
        />
      )}
      {visibleDataSourcesDeleteModal && (
        <DataSourceDelete
          data={selectedDataSource}
          handleClose={() => handleAction()}
          handleProcess={(status) => handleDeleteProcess(status)}
        />
      )}
    </>
  );
};

export default DataSourcesPage;
